// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
import { transparency } from '../../tokens/primitiveColors/transparency';
import { background } from '../../tokens/themeColors/background';
import { border } from '../../tokens/themeColors/border';
import { gradient, toCssGradient } from '../../tokens/themeColors/gradient';
import { icon } from '../../tokens/themeColors/icon';
import { inverseBackground, inverseBorder, inverseIcon } from '../../tokens/themeColors/inverse';
// Figma: shares Button/* component color tokens
// (contained = Button/Primary/*, outlined+text = Button/Secondary/*)
// secondaryContained/secondaryOutlined/secondaryText mirror Button's grey-color
// secondary family (Secondary Icon Button component, node 15412:129558) -
// icon colors there follow the same mirroring convention since the Figma icon
// layers render as opaque image assets with no inspectable color variable.
// invertedContained/invertedOutlined/invertedText mirror Button's inverted family
// exactly (Icon Button - Inverse Theme component, node 15270:88505) - same
// Inverse/Button/Primary|Secondary/* Figma variables, same mode-flip rule: a
// light-mode inverted icon button looks like a dark-mode regular one, and
// vice versa - so `inverse*` (opposite of current mode) and `bg`/`bd`/`ic`
// (current mode, already declared below) are used together, not `inverse*` alone.
export var iconButtonColors = function(mode) {
    var bg = background(mode);
    var bd = border(mode);
    var ic = icon(mode);
    var gr = gradient(mode);
    // Contained uses inverse-mode tokens (dark bg in light mode)
    var inverseBg = inverseBackground(mode);
    var inverseBd = inverseBorder(mode);
    var inverseIc = inverseIcon(mode);
    return {
        // Mirrors buttonColors.ts's focus fix — see its comment (confirmed directly from
        // Button's own rendered CSS variable; not independently re-verified for IconButton
        // specifically due to Figma rate limits, but the two share the same focus-ring
        // contract everywhere else in this file).
        focus: {
            border: bd.focus
        },
        contained: {
            default: {
                background: inverseBg.level50,
                icon: inverseIc.heaviest
            },
            hover: {
                background: inverseBg.level300
            },
            active: {
                background: inverseBg.state.selected3,
                icon: inverseIc.heaviest
            },
            disabled: {
                background: bg.state.disabled2,
                icon: ic.disabled2
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabled3,
                icon: ic.disabled2
            }
        },
        outlined: {
            default: {
                border: bd.default,
                icon: ic.active
            },
            hover: {
                background: bg.state.hover0
            },
            active: {
                background: bg.state.selectedTransparent,
                border: bd.action200,
                icon: ic.actionAlt
            },
            disabled: {
                border: bd.soft,
                icon: ic.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                border: bd.heavy,
                icon: ic.disabled2
            }
        },
        text: {
            default: {
                icon: ic.active
            },
            hover: {
                background: bg.state.hover0
            },
            active: {
                background: bg.state.selectedTransparent,
                icon: ic.actionAlt
            },
            disabled: {
                icon: ic.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                icon: ic.disabled2
            }
        },
        secondaryContained: {
            default: {
                background: toCssGradient(gr.grayLinear.default),
                icon: ic.active
            },
            hover: {
                background: toCssGradient(gr.grayLinear.hover)
            },
            active: {
                background: toCssGradient(gr.grayLinear.active),
                icon: inverseIc.heaviest
            },
            disabled: {
                background: bg.state.disabled2,
                icon: ic.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabled3,
                icon: ic.disabled2
            }
        },
        // default/active icons here mirror Button's secondaryOutlined/secondaryText (ic.heavy
        // / ic.active) — NOT secondaryContained's pattern (ic.active / inverseIc.heaviest).
        // Confirmed against Button's own Figma-verified values (Button/Secondary/Outlined and
        // /Text Default/Icon -> Icon Colors/Heavy, Active/Icon -> Icon Colors/Active).
        secondaryOutlined: {
            default: {
                background: toCssGradient(gr.transparentLinear.default),
                border: bd.default,
                icon: ic.heavy
            },
            hover: {
                background: toCssGradient(gr.transparentLinear.hover)
            },
            active: {
                background: toCssGradient(gr.transparentLinear.active),
                border: bd.heavy,
                icon: ic.active
            },
            disabled: {
                border: bd.soft,
                icon: ic.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                border: bd.heavy,
                icon: ic.disabled2
            }
        },
        secondaryText: {
            default: {
                icon: ic.heavy
            },
            hover: {
                background: toCssGradient(gr.transparentLinear.hover)
            },
            active: {
                background: toCssGradient(gr.transparentLinear.active),
                icon: ic.active
            },
            disabled: {
                icon: ic.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                icon: ic.disabled2
            }
        },
        // Verified against the Inverse/Button/Primary/* alias chain directly (mirrors
        // buttonColors.ts's invertedContained — see its comment for detail).
        invertedContained: {
            default: {
                background: bg.level50,
                icon: ic.heavy
            },
            hover: {
                background: bg.level300,
                icon: ic.active
            },
            active: {
                background: bg.state.selected3,
                icon: ic.active
            },
            disabled: {
                background: inverseBg.state.disabled2,
                icon: inverseIc.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: inverseBg.state.disabled3,
                icon: inverseIc.disabled
            }
        },
        // Alias chain (mirrors buttonColors.ts's invertedOutlined — see its comment): Active
        // resolves via Inverse/Background Colors/State/Selected Transparent, Inverse/Border
        // Colors/Action 200, Inverse/Icon Colors/Action Alt.
        invertedOutlined: {
            default: {
                border: inverseBd.heavy,
                icon: inverseIc.heavy
            },
            hover: {
                background: inverseBg.state.hover0,
                border: inverseBd.heavy
            },
            active: {
                background: inverseBg.state.selectedTransparent,
                border: inverseBd.action200,
                icon: inverseIc.actionAlt
            },
            disabled: {
                border: inverseBd.soft,
                icon: inverseIc.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: inverseBg.state.disabledTransparent,
                border: inverseBd.heavy,
                icon: inverseIc.disabled2
            }
        },
        // NOTE: no Figma alias found under Inverse/Button/* for a "Text" variant - could not
        // verify against Figma Component Colors directly. Mirrors invertedOutlined.active
        // minus border (see buttonColors.ts's invertedText comment for the reasoning).
        invertedText: {
            default: {
                icon: inverseIc.heavy
            },
            hover: {
                background: inverseBg.state.hover0
            },
            active: {
                background: inverseBg.state.selectedTransparent,
                icon: inverseIc.actionAlt
            },
            disabled: {
                icon: inverseIc.disabled
            }
        },
        translucent: {
            default: {
                background: transparency.gray.dark[500],
                icon: ic.white
            },
            hover: {
                background: transparency.gray.dark[700]
            },
            active: {
                background: transparency.gray.dark[800],
                icon: ic.white
            },
            disabled: {
                background: transparency.gray.dark[300],
                icon: transparency.gray.light[500]
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: transparency.gray.dark[400],
                icon: transparency.gray.light[700]
            }
        }
    };
};

//# sourceMappingURL=iconButtonColors.js.map